import { Crosshair } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { SyncedLocation } from '@/types/api';

interface PinAddressCardProps {
  pinAddress: SyncedLocation['pinAddress'];
  /** What the partner printed (`address.full`), for the comparison line. */
  printed: string | null;
}

/**
 * The address the map says is AT the pin, beside the one the partner printed.
 *
 * Two addresses on purpose: one is observed (the partner wrote it), the other
 * derived (a reverse geocode found it within a few metres of the pin). When
 * they disagree, either the pin or the address is wrong, and this is where an
 * admin sees which before publishing a venue on the wrong street.
 */
export function PinAddressCard({ pinAddress, printed }: PinAddressCardProps): JSX.Element | null {
  if (!pinAddress) return null;
  const verdict = pinAddress.matchesPrinted;
  return (
    <Card data-testid="pin-address-card">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Crosshair className="h-5 w-5" />
          Address at the pin
          {verdict === true && <Badge variant="success">agrees</Badge>}
          {verdict === false && <Badge variant="warning" data-testid="address-mismatch-badge">disagrees with printed address</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <div>
          <span className="text-muted-foreground">At the pin: </span>
          <span data-testid="pin-address-text">{pinAddress.text}</span>
          {pinAddress.distanceM !== null && (
            <span className="text-muted-foreground"> ({pinAddress.distanceM.toLocaleString()} m from the pin)</span>
          )}
        </div>
        {printed !== null && (
          <div>
            <span className="text-muted-foreground">Printed by the partner: </span>
            <span>{printed}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
